import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Settings2 } from 'lucide-react';
import {
  listSystemMemberships,
  listSystemPlans,
  updateSystemMembershipPlan,
  updateSystemMembershipSubscription,
  type SystemDurationUnit,
  type SystemMembership,
  type SystemPlan,
  type SystemSubscriptionStatus,
} from '../api/client';
import { useDebugMode } from '../hooks/useDebugMode';
import { useError } from '../context/ErrorContext';
import { PANEL_BASE, panelPath } from '../config/paths';
import PageBackButton from './PageBackButton';
import { SkeletonTableRow } from './Skeleton';

const DURATION_PRESETS: { amount: number; unit: SystemDurationUnit; labelKey: string }[] = [
  { amount: 1, unit: 'week', labelKey: 'panel.systemDuration1Week' },
  { amount: 2, unit: 'week', labelKey: 'panel.systemDuration2Weeks' },
  { amount: 1, unit: 'month', labelKey: 'panel.systemDuration1Month' },
  { amount: 3, unit: 'month', labelKey: 'panel.systemDuration3Months' },
  { amount: 6, unit: 'month', labelKey: 'panel.systemDuration6Months' },
  { amount: 12, unit: 'month', labelKey: 'panel.systemDuration12Months' },
];

const SystemDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const hasDebugMode = useDebugMode();
  const { showError: showErrorPopup } = useError();
  const [memberships, setMemberships] = useState<SystemMembership[]>([]);
  const [plans, setPlans] = useState<SystemPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active'>('active');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState(1);
  const [customUnit, setCustomUnit] = useState<SystemDurationUnit>('month');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, planList] = await Promise.all([listSystemMemberships(), listSystemPlans()]);
      setMemberships(list);
      setPlans(planList);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('common.loadFailed');
      setError(msg);
      showErrorPopup(msg);
    } finally {
      setLoading(false);
    }
  }, [showErrorPopup, t]);

  useEffect(() => {
    if (hasDebugMode) load();
  }, [hasDebugMode, load]);

  const filtered = useMemo(() => {
    if (filter === 'all') return memberships;
    return memberships.filter(
      m =>
        m.subscriptionStatus === 'active' ||
        m.plan === 'goodmode' ||
        m.plan === 'enterprise' ||
        m.plan === 'pro'
    );
  }, [filter, memberships]);

  const selected = useMemo(
    () => memberships.find(m => m.id === selectedId) ?? null,
    [memberships, selectedId]
  );

  const applyMembershipUpdate = (userId: string, updated: SystemMembership) => {
    setMemberships(prev => prev.map(m => (m.id === userId ? updated : m)));
  };

  const handlePlanChange = async (userId: string, plan: string) => {
    setSavingId(userId);
    try {
      const updated = await updateSystemMembershipPlan(userId, plan);
      applyMembershipUpdate(userId, updated);
    } catch (err) {
      showErrorPopup(err);
    } finally {
      setSavingId(null);
    }
  };

  const handleSubscriptionUpdate = async (
    userId: string,
    status: SystemSubscriptionStatus,
    duration?: { amount: number; unit: SystemDurationUnit }
  ) => {
    setSavingId(userId);
    try {
      const updated = await updateSystemMembershipSubscription(userId, {
        status,
        ...(duration
          ? { durationAmount: duration.amount, durationUnit: duration.unit }
          : {}),
      });
      applyMembershipUpdate(userId, updated);
    } catch (err) {
      showErrorPopup(err);
    } finally {
      setSavingId(null);
    }
  };

  if (!hasDebugMode) {
    return <Navigate to={PANEL_BASE} replace />;
  }

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime()) || d.getTime() === 0) return '—';
      return d.toLocaleString();
    } catch {
      return '—';
    }
  };

  const saving = selected ? savingId === selected.id : false;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <PageBackButton to={panelPath('/system-details/overview')} className="mb-4" />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Settings2 className="h-7 w-7 text-primary-600" />
            {t('panel.systemDetailsTitle')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('panel.systemDetailsSubtitle')}
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-sm">
          <button
            type="button"
            onClick={() => setFilter('active')}
            className={
              filter === 'active'
                ? 'px-3 py-1.5 bg-primary-600 text-white'
                : 'px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            }
          >
            {t('panel.systemFilterActive')}
          </button>
          <button
            type="button"
            onClick={() => setFilter('all')}
            className={
              filter === 'all'
                ? 'px-3 py-1.5 bg-primary-600 text-white'
                : 'px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
            }
          >
            {t('panel.systemFilterAll')}
          </button>
        </div>
      </div>

      {error && (
        <p className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('panel.systemEmail')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('panel.systemPlan')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('panel.systemSubStatus')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('panel.systemRenewsAt')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                [...Array(5)].map((_, i) => <SkeletonTableRow key={i} cols={4} />)
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    {t('panel.systemNoMemberships')}
                  </td>
                </tr>
              ) : (
                filtered.map(m => (
                  <tr
                    key={m.id}
                    onClick={() => setSelectedId(m.id)}
                    className={
                      selectedId === m.id
                        ? 'bg-primary-50 dark:bg-primary-900/20 cursor-pointer'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer'
                    }
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{m.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">{m.plan}</span>
                      {m.effectivePlan !== m.plan && (
                        <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">
                          → {m.effectivePlan}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {m.subscriptionStatus || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {formatDate(m.subscriptionRenewsAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
            {t('panel.systemMembershipDetail')}
          </h2>
          {!selected ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('panel.systemSelectMembership')}</p>
          ) : (
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t('panel.systemEmail')}</div>
                <div className="text-gray-900 dark:text-white break-all">{selected.email}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">ID</div>
                <div className="font-mono text-xs text-gray-700 dark:text-gray-300 break-all">{selected.id}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t('panel.systemSubStatus')}</div>
                <div className="text-gray-900 dark:text-white">{selected.subscriptionStatus || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t('panel.systemRenewsAt')}</div>
                <div className="text-gray-900 dark:text-white">{formatDate(selected.subscriptionRenewsAt)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t('panel.systemCreatedAt')}</div>
                <div className="text-gray-900 dark:text-white">{formatDate(selected.createdAt)}</div>
              </div>
              {selected.lemonSubscriptionId ? (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{t('panel.systemLemonId')}</div>
                  <div className="font-mono text-xs text-gray-700 dark:text-gray-300 break-all">
                    {selected.lemonSubscriptionId}
                  </div>
                </div>
              ) : null}

              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1" htmlFor="system-plan-select">
                  {t('panel.systemChangePlan')}
                </label>
                <select
                  id="system-plan-select"
                  value={selected.plan}
                  disabled={saving}
                  onChange={e => handlePlanChange(selected.id, e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                >
                  {plans.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.id})
                    </option>
                  ))}
                </select>
              </div>

              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  {t('panel.systemSubscriptionActions')}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  {t('panel.systemActivateHint')}
                </p>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {DURATION_PRESETS.map(preset => (
                    <button
                      key={`${preset.amount}-${preset.unit}`}
                      type="button"
                      disabled={saving}
                      onClick={() =>
                        handleSubscriptionUpdate(selected.id, 'active', {
                          amount: preset.amount,
                          unit: preset.unit,
                        })
                      }
                      className="px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {t(preset.labelKey)}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-end gap-2 mb-3">
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1" htmlFor="system-custom-amount">
                      {t('panel.systemCustomDuration')}
                    </label>
                    <input
                      id="system-custom-amount"
                      type="number"
                      min={1}
                      max={customUnit === 'week' ? 104 : 24}
                      value={customAmount}
                      disabled={saving}
                      onChange={e => setCustomAmount(Math.max(1, Number(e.target.value) || 1))}
                      className="w-20 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
                    />
                  </div>
                  <select
                    value={customUnit}
                    disabled={saving}
                    onChange={e => setCustomUnit(e.target.value as SystemDurationUnit)}
                    className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
                    aria-label={t('panel.systemDurationUnit')}
                  >
                    <option value="week">{t('panel.systemUnitWeek')}</option>
                    <option value="month">{t('panel.systemUnitMonth')}</option>
                  </select>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      handleSubscriptionUpdate(selected.id, 'active', {
                        amount: customAmount,
                        unit: customUnit,
                      })
                    }
                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {t('panel.systemActivateCustom')}
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => handleSubscriptionUpdate(selected.id, 'expired')}
                    className="px-2.5 py-1 rounded-md text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    {t('panel.systemMarkExpired')}
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      handleSubscriptionUpdate(selected.id, 'cancelled', {
                        amount: customAmount,
                        unit: customUnit,
                      })
                    }
                    className="px-2.5 py-1 rounded-md text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-900/60 disabled:opacity-50"
                  >
                    {t('panel.systemMarkCancelled')}
                  </button>
                </div>
              </div>

              {saving && (
                <p className="text-xs text-gray-500">{t('common.saving')}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemDetailsPage;
